import React, { useState } from 'react';

import researchFundProjects from './researchFundProjectsData';

const impactGroups = [
  { title: 'Research Infrastructure', items: [['Facilities developed', '11']] },
  { title: 'Academic Research', items: [['Conference papers', '19'], ['Journal publications', '12'], ['Patents', '6'], ['Theses', '23']] },
  { title: 'Technology Development', items: [['Software solutions', '15'], ['Models & simulations', '10'], ['Hardware systems', '11'], ['Training kits', '4'], ['Prototypes', '10']] },
  { title: 'Data-Driven Research', items: [['Data & inventories', '9'], ['Models & maps', '14']] },
  { title: 'Capacity Building', items: [['Internships', '80'], ['Workshops & trainings', '52'], ['Surveys', '2'], ['Seminars & webinars', '31']] },
  { title: 'Outreach & Collaboration', items: [['Media products & publications', '17'], ['Education outreach programs', '6'], ['Academia-industry collaborations', '13'], ['National collaborations', '40'], ['International collaborations', '14']] },
];

const projectGalleryCounts = [6, 6, 4, 4, 6, 6, 6, 6, 3, 4, 5, 6, 6];

const projectGalleryImages = (projectIndex) => Array.from(
  { length: projectGalleryCounts[projectIndex] || 0 },
  (_, imageIndex) => `/research-fund/project-${String(projectIndex + 1).padStart(2, '0')}/gallery-${imageIndex + 1}.jpg`,
);

const ResearchFundProjects = () => {
  const [openProjects, setOpenProjects] = useState(new Set());
  const domains = [...new Set(researchFundProjects.map((project) => project.domain))];

  const toggleProject = (index) => {
    setOpenProjects((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <>
      <section className="research-projects" aria-labelledby="research-projects-title">
        <div className="research-projects-inner">
          <header className="research-projects-header">
            <p className="section-eyebrow">RESEARCH FUND PORTFOLIO</p>
            <h2 id="research-projects-title">Funded Projects</h2>
            <div className="research-fund-title-line" aria-hidden="true" />
            <p>
              Explore the projects supported through the NCGSA Research Fund. Expand any project
              to view its partners, executive summary, Sustainable Development Goals, and gallery.
            </p>
          </header>

          <div className="research-project-domains">
            {domains.map((domain) => (
              <section className="research-project-domain" key={domain}>
                <div className="research-project-domain-title">
                  <span>{String(domains.indexOf(domain) + 1).padStart(2, '0')}</span>
                  <h3>{domain}</h3>
                </div>
                <div className="research-project-list">
                  {researchFundProjects.map((project, index) => {
                    if (project.domain !== domain) return null;
                    const isOpen = openProjects.has(index);
                    const panelId = `research-project-panel-${index}`;
                    return (
                      <article className={`research-project-card${isOpen ? ' is-open' : ''}`} key={project.title}>
                        <button
                          className="research-project-toggle"
                          type="button"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => toggleProject(index)}
                        >
                          <span className="research-project-number">{String(index + 1).padStart(2, '0')}</span>
                          <span className="research-project-toggle-copy">
                            <strong>{project.title}</strong>
                            <small>{project.academia} · {project.industry}</small>
                          </span>
                          <span className="research-project-plus" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                        </button>

                        {isOpen && (
                          <div className="research-project-panel" id={panelId}>
                            <div className="research-project-partners">
                              {project.logos.map((logo, logoIndex) => (
                                <figure key={logo}>
                                  <img src={logo} alt={`${logoIndex === 0 ? project.academia : project.industry} logo`} />
                                </figure>
                              ))}
                              <div className="research-project-partner-copy">
                                <p><span>Academic partner</span>{project.academia}</p>
                                <p><span>Industry partner</span>{project.industry}</p>
                              </div>
                            </div>

                            <div className="research-project-summary">
                              <h4>Executive Summary</h4>
                              <p>{project.summary}</p>
                            </div>

                            {project.sdgImages.length > 0 && (
                              <div className="research-project-sdgs">
                                <h4>Sustainable Development Goals</h4>
                                <div>
                                  {project.sdgImages.map((image, sdgIndex) => (
                                    <img key={image} src={image} alt={`SDG ${project.sdgs[sdgIndex]}`} />
                                  ))}
                                </div>
                              </div>
                            )}

                            {project.gallery && (
                              <div className="research-project-gallery">
                                <div className="research-project-gallery-heading">
                                  <h4>Project Gallery</h4>
                                </div>
                                <div className="research-project-gallery-grid">
                                  {projectGalleryImages(index).map((image, imageIndex) => (
                                    <a href={image} target="_blank" rel="noreferrer" key={image}>
                                      <img
                                        src={image}
                                        alt={`${project.title} — project visual ${imageIndex + 1}`}
                                        loading="lazy"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="research-fund-outcomes" aria-labelledby="research-fund-outcomes-title">
        <div className="research-fund-outcomes-inner">
          <header>
            <p className="section-eyebrow">MEASURABLE PROGRESS</p>
            <h2 id="research-fund-outcomes-title">Outcomes &amp; Strategic Impact</h2>
            <div className="research-fund-title-line" aria-hidden="true" />
            <p>
              The Research Fund has strengthened Pakistan’s research infrastructure, technological
              self-reliance, data-driven decision-making, capacity building, and public engagement
              in space and geospatial technologies.
            </p>
          </header>
          <div className="research-fund-impact-grid">
            {impactGroups.map((group) => (
              <article key={group.title}>
                <h3>{group.title}</h3>
                <div>
                  {group.items.map(([label, value]) => (
                    <p key={label}><strong>{value}</strong><span>{label}</span></p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ResearchFundProjects;
